"use client"

import { useRouter } from "next/navigation"
import {
  DriverProfile,
  FilterStats,
  SearchParamsType,
  FilterValues,
  VerificationType,
  ExperienceLevel,
  WonShipmentsRange,
} from "@/lib/drivers/types"
import { DriverCard } from "./driver-card"
import { DriverFiltersClient } from "./driver-filters-client"
import type { ServiceName } from "@/lib/drivers/types"
import { recordToURLParams, fromURLFilterValue } from "@/lib/drivers/url-params"

export interface DriverListClientProps {
  profiles: DriverProfile[]
  filterStats: FilterStats
  currentPage: number
  totalPages: number
  itemsPerPage: number
  searchParams: SearchParamsType
  totalDrivers: number
}

export function DriverListClient({
  profiles,
  filterStats,
  currentPage,
  totalPages,
  searchParams,
}: DriverListClientProps) {
  const router = useRouter()

  const handlePageChange = (newPage: number) => {
    const params = {
      ...searchParams,
      page: newPage.toString(),
    }

    const urlParams = recordToURLParams(params)

    router.replace(`/drivers?${urlParams.toString()}`, { scroll: false })
  }

  const activeFilters: FilterValues = {
    verifications: (searchParams.verifications || "")
      .split(",")
      .filter(Boolean)
      .filter((v): v is VerificationType =>
        [
          "backgroundCheck",
          "insurance",
          "identity",
          "usdot",
          "usda",
          "manual",
        ].includes(v),
      ),
    shipmentTypes: (searchParams.shipmentTypes || "")
      .split(",")
      .filter(Boolean)
      .map((type) => {
        return type
      }) as ServiceName[],
    rating: searchParams.rating ? Number(searchParams.rating) : null,
    experience:
      searchParams.experience &&
      ["new", "intermediate", "expert"].includes(searchParams.experience)
        ? (searchParams.experience as ExperienceLevel)
        : null,
    wonShipments:
      searchParams.wonShipments &&
      ["lessThan10", "between10And50", "moreThan50"].includes(
        searchParams.wonShipments,
      )
        ? (searchParams.wonShipments as WonShipmentsRange)
        : null,
    shipmentCategories: searchParams?.shipmentCategories || null,
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col md:flex-row gap-8">
        {/* Filters Sidebar */}
        <aside className="w-full md:w-1/4">
          <DriverFiltersClient
            stats={filterStats}
            activeFilters={activeFilters}
            searchParams={searchParams}
          />
        </aside>

        {/* Main Content */}
        <main className="w-full md:w-3/4">
          {profiles.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {profiles.map((profile) => (
                  <DriverCard key={profile.data.id} profile={profile} />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex justify-center items-center space-x-4 mt-8">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-4 py-2 bg-gray-100 rounded-md disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span>
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 bg-gray-100 rounded-md disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <h3 className="text-xl font-semibold mb-2">No drivers found</h3>
              <p className="text-gray-600">
                Try adjusting your filters to see more results.
              </p>
            </div>
          )}
        </main>
      </div>
    </div>
  )
}
